<template>
  <div class="event-page">
    <router-link to="/" class="back-link"><i class="fa-solid fa-arrow-left"></i> Retour au calendrier</router-link>

    <div v-if="loading" class="loading">Chargement...</div>

    <div v-else-if="!event" class="no-events">
      <p>Événement introuvable.</p>
    </div>

    <div v-else class="event-card">
      <div class="event-view">
        <img v-if="getEventImage(event) && !imageFailed" :src="getEventImage(event)!" alt="Affiche du tournoi"
          class="event-image" @error="imageFailed = true" />
        <div v-else class="event-image poster-placeholder">
          <i class="fa-solid fa-image"></i>
          <span>Affiche non disponible</span>
        </div>

        <div class="event-info">
          <h1 class="event-title">{{ displayEventName(event) }}</h1>

          <p class="event-meta">
            <span><i class="fa-solid fa-calendar-days"></i> {{ formatEventDateRange(event.date, event.date_end) }}</span>
            <span v-if="event.organiser"><i class="fa-solid fa-shield-halved"></i> {{ event.organiser }}</span>
            <span v-if="event.place"><i class="fa-solid fa-location-dot"></i> {{ titleCase(event.place) }}</span>
          </p>
          <a v-if="event.address" :href="mapsLink(event.address)" target="_blank" rel="noopener noreferrer"
            class="address-link">
            <i class="fa-solid fa-map-location-dot"></i> {{ event.address }}
          </a>
          <span v-if="formatPriceParts(event).length" class="price-chip">
            <span v-for="part in formatPriceParts(event)" class="price-part" :key="part">{{ part }}</span>
          </span>
          <p v-if="canRegister(event) && paymentModesLabel(event)" class="deadline-info">
            <i class="fa-solid fa-euro-sign"></i> {{ paymentModesLabel(event) }}
          </p>
          <p v-if="event.registration_deadline && event.registration_status !== 'closed'" class="deadline-info"
            :class="{ passed: deadlinePassed(event) }">
            <i class="fa-solid" :class="deadlinePassed(event) ? 'fa-lock' : 'fa-hourglass-half'"></i>
            {{ deadlinePassed(event) ? 'Inscriptions clôturées depuis le' : 'Inscriptions jusqu\'au' }}
            {{ formatDeadline(event.registration_deadline) }}
          </p>
          <p v-if="event.description" class="event-description">{{ event.description }}</p>

          <div class="event-actions">
            <router-link v-if="canRegister(event)" :to="registrationPath(event)" class="btn-primary">
              S'inscrire
            </router-link>
            <a v-if="event.external_link" :href="resolveExternalLink(event.external_link)!" target="_blank"
              rel="noopener noreferrer" class="btn-outline">
              {{ event.external_link_label || "Plus d'infos" }}
            </a>
            <a :href="calendarLink.href" :download="calendarLink.download" target="_blank" rel="noopener noreferrer"
              class="btn-outline">
              Ajouter à mon calendrier
            </a>
            <button class="btn-outline" @click="shareLink">
              <i class="fa-solid" :class="linkCopied ? 'fa-check' : 'fa-share-nodes'"></i>
              {{ linkCopied ? 'Lien copié' : 'Partager' }}
            </button>
            <router-link :to="`/event-roster/${event.id}`" class="btn-outline">
              Voir les inscriptions
            </router-link>
            <router-link v-if="hasLeaderboard" :to="`${eventPath(event)}/classement`" class="btn-outline">
              Voir le classement
            </router-link>
            <!-- Un seul point d'entrée pour tout ce qui se gère (infos du tournoi local,
            affiche, inscriptions, paiement, pointage) : la page de gestion, onglet
            Paramètres en tête pour un tournoi local dont on peut éditer les infos. -->
            <router-link v-if="event.can_manage || (isLocal && canManageLocal)"
              :to="{ path: `/event-inscriptions/${event.id}`, query: isLocal ? { tab: 'parametres' } : {} }"
              class="btn-primary">
              Gérer mon tournoi
            </router-link>
            <button v-if="isSuperAdmin && event.organizer_club_id" class="btn-outline" :disabled="togglingEventPremium"
              @click="toggleEventPremium">
              {{ event.premium_events_enabled
                ? `✓ Premium actif pour ${event.organiser} (désactiver)`
                : `Activer le premium pour ${event.organiser}` }}
            </button>
            <p v-else-if="isSuperAdmin" class="admin-hint">
              Pas de club organisateur assigné à cet événement : impossible d'activer le premium (personne ne
              pourrait gérer les inscriptions).
            </p>
            <button v-if="isLocal && canManageLocal" class="btn-outline btn-danger" @click="confirmDeleteLocal">
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Encart organisateurs : chaque fiche partagée (coupe, open...) est vue par des
    dirigeants de club et de comité, on les renvoie vers la présentation des services.
    Masqué pour ceux qui gèrent déjà ce tournoi. -->
    <router-link v-if="event && !event.can_manage" to="/services" class="organiser-promo">
      <i class="fa-solid fa-bullseye organiser-promo-icon"></i>
      <span class="organiser-promo-body">
        <strong v-if="event.premium_events_enabled">Inscriptions gérées avec DartsHub</strong>
        <strong v-else>Vous organisez un tournoi ?</strong>
        <span>Inscriptions en ligne, paiement HelloAsso sans commission, pointage le jour J et export vers
          votre logiciel.</span>
      </span>
      <span class="organiser-promo-cta">Découvrir <i class="fa-solid fa-arrow-right"></i></span>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import backendApi from '@/axios/backendApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { getCalendarLink } from '@/functions/addToCalendar';
import { formatEventDateRange, titleCase } from '@/functions/utils';
import {
  canRegister, deadlinePassed, displayEventName, eventTitle, formatDeadline, formatPriceParts, getEventImage, paymentModesLabel,
  resolveExternalLink, toOfficialEvent,
  eventPath, registrationPath, parseEventId,
} from '@/functions/eventDisplay';
import { applySeo, setJsonLd, SITE_URL } from '@/functions/seo';
import { hasRankingEntries } from '@/functions/leaderboards';

// Page dédiée d'un événement, publique (pas besoin d'être connecté pour voir affiche,
// date, prix, lieu) : remplace l'ancienne modale de détails du calendrier, qui n'avait
// pas d'URL propre et ne pouvait donc pas être partagée. Seule l'inscription elle-même
// (bouton "S'inscrire" -> /inscription/:id) demande un compte, via sa propre porte
// d'entrée (cf. EventRegistrationForm.vue).
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const event = ref<OfficialEvent | null>(null);
const loading = ref(true);
const imageFailed = ref(false);

const eventId = computed(() => parseEventId(route.params.eventId));
const isLocal = computed(() => (event.value as any)?.event_type === 'local');

// `isSuperAdmin` seul pour le toggle premium. Un tournoi local n'est géré (édition,
// suppression) que par son organisateur ou le super_admin (règle du 2026-09-24, miroir
// de events/routes.py::_require_local_event_manager) : le rôle comité n'y donne aucun
// droit, contrairement aux événements officiels.
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'));

const userClubName = ref('');
const fetchMyClub = async () => {
  try {
    const { data } = await backendApi.get('/clubs/my-club');
    userClubName.value = data.name || '';
  } catch { /* pas de club rattaché -- pas grave */ }
};
const canManageLocal = computed(() => !!event.value && (
  isSuperAdmin.value
  || event.value.created_by_email === authStore.email
  || (!!userClubName.value && event.value.organiser === userClubName.value)
));

const calendarLink = computed(() => event.value ? getCalendarLink(event.value) : { href: '#' });

// Recherche Google Maps sur l'adresse exacte : ouvre l'appli Maps sur mobile.
const mapsLink = (address: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

// Partage : Web Share API sur mobile quand elle existe, sinon copie du lien dans le
// presse-papier -- c'est tout l'intérêt de cette page par rapport à l'ancienne modale.
const linkCopied = ref(false);
const shareLink = async () => {
  const url = window.location.href;
  const title = event.value ? displayEventName(event.value) : 'Tournoi';
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return;
    } catch { /* annulé par l'utilisateur, on retombe sur la copie */ }
  }
  try {
    await navigator.clipboard.writeText(url);
    linkCopied.value = true;
    setTimeout(() => { linkCopied.value = false; }, 1500);
  } catch {
    toast.info(url);
  }
};

const togglingEventPremium = ref(false);
const toggleEventPremium = async () => {
  if (!event.value) return;
  const next = !event.value.premium_events_enabled;
  togglingEventPremium.value = true;
  try {
    await backendApi.patch(`/events/${event.value.id}/premium-events`, null, {
      params: { enabled: next },
      headers: { Authorization: `Bearer ${authStore.token}` },
    });
    event.value.premium_events_enabled = next;
    toast.success(next ? 'Accès premium activé pour cet événement.' : 'Accès premium désactivé pour cet événement.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    togglingEventPremium.value = false;
  }
};

const confirmDeleteLocal = async () => {
  if (!event.value || !canManageLocal.value) return;
  if (!confirm(`Supprimer "${eventTitle(event.value)}" ?`)) return;
  try {
    await backendApi.delete(`/events/${event.value.id}`);
    toast.success('Tournoi supprimé.');
    router.push({ path: '/', query: { tab: 'local' } });
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la suppression.');
  }
};

// Bouton "Voir le classement" seulement quand des résultats ont été importés pour cet
// événement (même détection que CMER/OfficialCalendar.vue).
const hasLeaderboard = ref(false);
const checkLeaderboard = async () => {
  try {
    const { data } = await backendApi.get(`/leaderboard/event/${eventId.value}`);
    hasLeaderboard.value = hasRankingEntries(data?.leaderboard);
  } catch {
    hasLeaderboard.value = false;
  }
};

// Seul un vrai 404 de l'API vaut "introuvable" (noindex). Un échec réseau ou un 5xx
// pendant le rendu de Googlebot ne doit pas écraser le <head> rendu côté serveur.
const notFound = ref(false);

const loadEvent = async () => {
  loading.value = true;
  try {
    const { data } = await backendApi.get(`/events/${eventId.value}`);
    event.value = toOfficialEvent(data);
    notFound.value = false;
  } catch (err: any) {
    event.value = null;
    notFound.value = err?.response?.status === 404;
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  const tasks: Promise<unknown>[] = [
    loadEvent(),
    checkLeaderboard(),
  ];
  if (authStore.isAuthenticated) tasks.push(fetchMyClub());
  await Promise.all(tasks);
  updateSeo();
});

onUnmounted(() => setJsonLd('event', null));

// Titre/description propres à l'événement, et données structurées schema.org/SportsEvent
// (résultats enrichis "événements" de Google). Un événement de démo n'est jamais indexé,
// comme il n'apparaît pas au calendrier public.
const updateSeo = () => {
  if (!event.value) {
    if (notFound.value) applySeo({ title: 'Événement introuvable', path: route.path, noindex: true });
    return;
  }
  const ev = event.value;
  // Ancien lien "/evenement/46" (ou nom/ville changés depuis) : on remplace par l'URL
  // lisible à jour, sans nouvelle entrée d'historique.
  const path = eventPath(ev);
  if (route.path !== path) router.replace({ path, query: route.query, hash: route.hash });
  const name = displayEventName(ev);
  const place = ev.place ? titleCase(ev.place) : null;
  const dateLabel = formatEventDateRange(ev.date, ev.date_end);
  const summary = [`Tournoi de fléchettes le ${dateLabel}`, place && `à ${place}`, ev.organiser && `organisé par ${ev.organiser}`]
    .filter(Boolean).join(' ');
  const description = `${summary}. Infos, tarifs et inscription en ligne.`;
  const image = getEventImage(ev);

  applySeo({ title: `${name} - ${dateLabel}`, description, path, image, noindex: (ev as any).event_type === 'demo' });
  setJsonLd('event', {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name,
    sport: 'Fléchettes',
    startDate: ev.date,
    endDate: ev.date_end || ev.date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    url: `${SITE_URL}${path}`,
    description: ev.description || description,
    ...(image ? { image: [image] } : {}),
    ...(place || ev.address
      ? { location: { '@type': 'Place', name: place || ev.address, address: ev.address || place } }
      : {}),
    ...(ev.organiser ? { organizer: { '@type': 'Organization', name: ev.organiser } } : {}),
    ...(ev.price_cents != null
      ? {
        offers: {
          '@type': 'Offer',
          price: (ev.price_cents / 100).toFixed(2),
          priceCurrency: 'EUR',
          url: `${SITE_URL}${registrationPath(ev)}`,
          availability: canRegister(ev) ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
        },
      }
      : {}),
  });
};
</script>

<style scoped>
.event-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--color-fg-darker);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.back-link:hover {
  color: var(--color-accent);
}

.no-events,
.loading {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-fg-darker);
}

.event-card {
  background: var(--color-bg-lighter);
  border: 1px solid var(--color-light-shadow);
  border-radius: var(--radius);
  padding: 1.75rem;
  box-sizing: border-box;
}

.event-view {
  display: flex;
  gap: 1.75rem;
  align-items: flex-start;
}

.event-image {
  flex: 0 0 280px;
  width: 280px;
  max-height: 400px;
  object-fit: contain;
  border-radius: var(--radius);
  background: var(--color-bg);
  display: block;
  box-sizing: border-box;
}

.poster-placeholder {
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: 1px dashed var(--color-light-shadow);
  color: var(--color-fg-darker);
  font-size: 0.85rem;
}

.poster-placeholder i {
  font-size: 1.8rem;
  color: var(--color-accent);
}

.event-info {
  flex: 1 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.event-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
}

.event-meta {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  font-size: 0.9rem;
  color: var(--color-fg-darker);
}

.event-meta i {
  color: var(--color-accent);
  margin-right: 0.3rem;
}

.address-link {
  align-self: flex-start;
  font-size: 0.9rem;
  color: var(--color-accent);
  text-decoration: none;
}

.address-link i {
  margin-right: 0.3rem;
}

.event-description {
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-fg-darker);
  white-space: pre-line;
}

.price-chip {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-accent);
  background: rgba(245, 165, 36, 0.12);
  padding: 0.3rem 0.7rem;
  border-radius: var(--radius);
}

/* Une catégorie par ligne : sur une seule ligne, les tarifs débordaient. */
.price-part {
  display: block;
}

.deadline-info {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-fg-darker);
}

.deadline-info i {
  color: var(--color-accent);
  margin-right: 0.3rem;
}

.deadline-info.passed {
  color: var(--color-error, #c0392b);
}

.deadline-info.passed i {
  color: var(--color-error, #c0392b);
}

.event-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.6rem;
  width: 100%;
  margin-top: 0.5rem;
}

.admin-hint {
  font-size: 0.8rem;
  color: var(--color-fg-darker);
  margin: 0;
}

.btn-primary,
.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.6rem 1.1rem;
  border-radius: var(--radius);
  font-weight: 700;
  font-size: 0.85rem;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.btn-primary {
  background: var(--color-accent);
  color: var(--color-bg);
}

.btn-outline {
  background: transparent;
  border-color: var(--color-light-shadow);
  color: var(--color-fg);
}

.btn-outline:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.btn-outline:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-danger {
  border-color: var(--color-danger, #d9534f);
  color: var(--color-danger, #d9534f);
}

.btn-danger:hover {
  background: var(--color-danger, #d9534f);
  color: #fff;
}

.organiser-promo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border: 1px dashed var(--color-light-shadow);
  border-radius: var(--radius);
  background: color-mix(in srgb, var(--color-accent) 6%, transparent);
  color: var(--color-fg);
  text-decoration: none;
  transition: border-color 0.2s;
}

.organiser-promo:hover {
  border-color: var(--color-accent);
}

.organiser-promo-icon {
  font-size: 1.5rem;
  color: var(--color-accent);
  flex-shrink: 0;
}

.organiser-promo-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.82rem;
  color: var(--color-fg-darker);
}

.organiser-promo-body strong {
  font-size: 0.92rem;
  color: var(--color-fg);
}

.organiser-promo-cta {
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--color-accent);
  white-space: nowrap;
}

@media (max-width: 640px) {
  .organiser-promo {
    flex-wrap: wrap;
  }

  .organiser-promo-cta {
    width: 100%;
    text-align: right;
  }

  .event-page {
    padding-left: 0;
    padding-right: 0;
  }

  .event-card {
    padding: 1.1rem;
  }

  .event-view {
    flex-direction: column;
    align-items: stretch;
  }

  .event-image {
    flex: 0 0 auto;
    width: 100%;
    max-height: 320px;
  }

  .poster-placeholder {
    height: 200px;
  }

  .event-info {
    align-items: stretch;
  }

  .event-title {
    text-align: center;
  }

  .event-meta {
    justify-content: center;
  }

  .price-chip {
    align-self: center;
    text-align: center;
  }
}
</style>
